import React from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';

export interface WizardPopupSystemProps {
  // Field popup
  openPopupId: string | null;
  popups?: any[];
  onClosePopup: () => void;
  onPopupButtonClick: (button: any) => void;
  saving: boolean;
  isMobile: boolean;
  // Exit popup
  showExitPopup: boolean;
  exitPopupId?: string;
  onCloseExitPopup: () => void;
  onConfirmedExit: () => void;
  exitPopupContent: { line1: string; line2: string };
}

export const WizardPopupSystem: React.FC<WizardPopupSystemProps> = ({
  openPopupId,
  popups,
  onClosePopup,
  onPopupButtonClick,
  saving,
  isMobile,
  showExitPopup,
  exitPopupId,
  onCloseExitPopup,
  onConfirmedExit,
  exitPopupContent,
}) => {
  const continueLabel = 'Continue';
  const exitLabel = 'Exit';

  const dialogPaperSx = {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      border: '1px solid #3A7380',
      '@media (min-width: 368px) and (max-width: 699px)': { maxHeight: '85vh' },
      '@media (max-width: 367px)': { maxHeight: '85vh' },
    },
  };

  const dialogContentSx = {
    p: 4,
    textAlign: 'center',
    backgroundColor: '#E5F0F1',
    overflow: 'auto',
    '@media (min-width: 368px) and (max-width: 699px)': { p: 2 },
    '@media (max-width: 367px)': { p: 1.5 },
  };

  const popupTextSx = {
    color: '#3A7380',
    fontFamily: '"Open Sans", sans-serif',
    mb: 3,
    fontSize: '1rem',
    lineHeight: 1.6,
    '@media (min-width: 368px) and (max-width: 699px)': {
      mb: 2,
      fontSize: '0.875rem',
      lineHeight: 1.5,
      overflowWrap: 'break-word',
      wordBreak: 'normal',
      hyphens: 'auto',
      '& p': { display: 'inline', margin: 0 },
      '& p::after': { content: '" "' },
      '& br': { display: 'none' },
    },
    '@media (max-width: 367px)': {
      mb: 1,
      fontSize: '0.8rem',
      lineHeight: 1.4,
      overflowWrap: 'break-word',
      '& p': { display: 'inline', margin: 0 },
      '& p::after': { content: '" "' },
      '& br': { display: 'none' },
      wordBreak: 'normal',
      hyphens: 'auto',
    },
  };

  const exitTextLineSx = (isLast: boolean) => ({
    color: '#3A7380',
    fontFamily: '"Open Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: 1.6,
    mb: isLast ? 3 : 1,
    '@media (min-width: 368px) and (max-width: 699px)': {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      mb: isLast ? 2 : 0.75,
      overflowWrap: 'break-word',
      wordBreak: 'normal',
      hyphens: 'auto',
      '& p': { display: 'inline', margin: 0 },
      '& p::after': { content: '" "' },
      '& br': { display: 'none' },
    },
    '@media (max-width: 367px)': {
      fontSize: '0.8rem',
      lineHeight: 1.4,
      mb: isLast ? 1 : 0.4,
      overflowWrap: 'break-word',
      wordBreak: 'normal',
      hyphens: 'auto',
      '& p': { display: 'inline', margin: 0 },
      '& p::after': { content: '" "' },
      '& br': { display: 'none' },
    },
  });

  const dialogActionsSx = {
    p: 3,
    pt: 0,
    justifyContent: 'center',
    flexDirection: 'row' as const,
    gap: 2,
    backgroundColor: '#E5F0F1',
    '@media (min-width: 368px) and (max-width: 699px)': { p: 2, gap: 1.5 },
    '@media (max-width: 367px)': { p: 1.5, gap: 1 },
  };

  return (
    <>
      {/* Field Popup Dialog */}
      {openPopupId && popups && (() => {
        const popup = popups.find((p: any) => p.id === openPopupId);
        if (!popup) return null;

        return (
          <Dialog
            open={true}
            onClose={(_, reason) => { if (reason !== 'backdropClick') onClosePopup(); }}
            maxWidth="sm"
            fullWidth
            sx={dialogPaperSx}
          >
            <DialogContent sx={dialogContentSx}>
              {popup.content && (
                <Box
                  sx={popupTextSx}
                  dangerouslySetInnerHTML={{ __html: popup.content }}
                />
              )}
            </DialogContent>
            <DialogActions
              sx={{
                p: 3,
                pt: 0,
                justifyContent: isMobile ? 'center' : (popup.buttonLayout === 'space-between' ? 'space-between' : 'center'),
                flexDirection: isMobile ? 'column' : (popup.buttonLayout === 'stack' ? 'column' : 'row'),
                alignItems: 'center',
                gap: 2,
                flexWrap: isMobile ? 'nowrap' : (popup.buttonLayout === 'stack' ? 'nowrap' : 'wrap'),
                backgroundColor: '#E5F0F1',
                '@media (min-width: 368px) and (max-width: 699px)': { p: 2, gap: 1.5 },
                '@media (max-width: 367px)': { p: 1.5, gap: 1 },
              }}
            >
              {popup.buttons
                .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                .map((button: any) => (
                  <Button
                    key={button.id}
                    variant="contained"
                    onClick={() => onPopupButtonClick(button)}
                    disabled={saving}
                    sx={{
                      borderRadius: '10px',
                      minWidth: '120px',
                      ...(isMobile || popup.buttonLayout === 'stack' ? { width: '100%', maxWidth: '200px' } : {}),
                      height: button.buttonHeight === 'small' ? '36px' : button.buttonHeight === 'large' ? '48px' : '40px',
                      fontFamily: '"Open Sans", sans-serif',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      textAlign: 'center',
                      ...(button.buttonStyle === 'secondary' ? {
                        backgroundColor: '#D7D7D7',
                        color: '#3A7380',
                        '&:hover': { backgroundColor: '#C0C0C0' },
                      } : {
                        backgroundColor: '#3A7380',
                        color: 'white',
                        '&:hover': { backgroundColor: '#2e5c66' },
                      }),
                      '@media (min-width: 368px) and (max-width: 699px)': { width: '180px', height: '38px', fontSize: '14px' },
                      '@media (max-width: 367px)': { width: '130px', height: '32px', fontSize: '12px' },
                    }}
                  >
                    {button.label}
                  </Button>
                ))}
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Exit Confirmation Popup */}
      {showExitPopup && exitPopupId && popups && (() => {
        const exitPopup = popups.find((p: any) => p.id === exitPopupId);
        if (!exitPopup) return null;

        return (
          <Dialog
            open={true}
            onClose={(_, reason) => { if (reason !== 'backdropClick') onCloseExitPopup(); }}
            maxWidth="sm"
            fullWidth
            sx={dialogPaperSx}
          >
            <DialogContent sx={dialogContentSx}>
              <Typography
                sx={{
                  color: '#3A7380',
                  fontFamily: '"Open Sans", sans-serif',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  lineHeight: 1.6,
                  mb: 2,
                  '@media (min-width: 368px) and (max-width: 699px)': {
                    fontSize: '0.95rem',
                    lineHeight: 1.5,
                    mb: 1.25,
                    overflowWrap: 'break-word',
                    wordBreak: 'normal',
                    hyphens: 'auto',
                    '& p': { display: 'inline', margin: 0 },
                    '& p::after': { content: '" "' },
                    '& br': { display: 'none' },
                  },
                  '@media (max-width: 367px)': {
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                    mb: 0.75,
                    overflowWrap: 'break-word',
                    wordBreak: 'normal',
                    hyphens: 'auto',
                    '& p': { display: 'inline', margin: 0 },
                    '& p::after': { content: '" "' },
                    '& br': { display: 'none' },
                  },
                }}
              >
                Are you sure you want to exit?
              </Typography>
              <Typography sx={exitTextLineSx(false)}>
                {exitPopupContent.line1}
              </Typography>
              <Typography sx={exitTextLineSx(true)}>
                {exitPopupContent.line2}
              </Typography>
            </DialogContent>
            <DialogActions sx={dialogActionsSx}>
              <Button
                variant="contained"
                onClick={onCloseExitPopup}
                sx={{
                  borderRadius: '10px',
                  width: '220px',
                  fontFamily: '"Open Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: '16px',
                  backgroundColor: '#3A7380',
                  color: 'white',
                  '&:hover': { backgroundColor: '#2e5c66' },
                  '@media (min-width: 368px) and (max-width: 699px)': { width: '155px', height: '38px', fontSize: '14px' },
                  '@media (max-width: 367px)': { width: '135px', height: '32px', fontSize: '12px' },
                }}
              >
                {continueLabel}
              </Button>
              <Button
                variant="contained"
                onClick={onConfirmedExit}
                disabled={saving}
                sx={{
                  borderRadius: '10px',
                  width: '220px',
                  fontFamily: '"Open Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: '16px',
                  backgroundColor: '#D7D7D7',
                  color: '#3A7380',
                  '&:hover': { backgroundColor: '#C0C0C0' },
                  '@media (min-width: 368px) and (max-width: 699px)': { width: '155px', height: '38px', fontSize: '14px' },
                  '@media (max-width: 367px)': { width: '135px', height: '32px', fontSize: '12px' },
                }}
              >
                {exitLabel}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </>
  );
};
